import {
  fetchCampaignData,
  widgetBootstrap
} from '../../services/cx-module-helper';
import Component from '@cx-modules/value-bet';
import ValueBetCss from '@cx-modules/value-bet/style.css?inline';
import { commaSeparatedValToArrOrVal } from '../../utils/helpers/splitStringToArray.js';

import loader from './assets/loader';
import themes from './themes/index.js';
import themeContract from './themes/contract';
import { meta } from './meta.js';

const { VITE_Q4_API_HOST: Q4ApiHost } = import.meta.env;
export const widgetName = meta.name;

const loadExternalFonts = () => {
  const css1 = document.createElement('link');
  css1.href =
    'https://fonts.googleapis.com/css?family=Roboto:100,100i,300,300i,400,400i,500,500i,700,700i,900,900i';
  css1.rel = 'stylesheet';

  const css2 = document.createElement('link');
  css2.href =
    'https://fonts.googleapis.com/css2?family=Public+Sans:ital,wght@0,100..900;1,100..900&display=swap';
  css2.rel = 'stylesheet';

  document.head.append(css1, css2);
};

const loadCustomGoogleFont = (fontFamily: string) => {
  const fontExclusionList = ['fantastic-fox'];

  if (!fontExclusionList.includes(fontFamily?.toLowerCase())) {
    const linkElement = document.createElement('link');
    linkElement.href = `https://fonts.googleapis.com/css?family=${fontFamily.replace(/ /g, '+')}:100,100i,300,300i,400,400i,500,500i,700,700i,900,900i`;
    linkElement.rel = 'stylesheet';
    document.head.appendChild(linkElement);
  }
};

type ContractParams = {
  affIds?: unknown;
  apiKey?: unknown;
  analytics?: { getDataLayer: () => unknown };
  campaignName?: unknown;
  categoryId?: unknown;
  colorBackground?: unknown;
  colorHeader?: unknown;
  colorHeaderText?: unknown;
  colorText?: unknown;
  eventId?: unknown;
  fontFamily?: string;
  geolocation?: unknown;
  groups?: unknown;
  hash?: unknown;
  marketType?: string;
  noDataAvailableFallbackText?: unknown;
  oddsFormat?: unknown;
  playerIds?: unknown;
  playerProps?: unknown;
  recommendedMarketId?: unknown;
  repub?: unknown;
  showNflLogosAndImages?: unknown;
  subeventId?: unknown;
  teamIds?: unknown;
  variation?: string;
};

type ContractProps = {
  analyticsImpressionEventData: unknown;
  affiliateKey?: unknown;
  affiliates?: unknown;
  categoryId?: unknown;
  color?: unknown;
  colorBackground?: unknown;
  colorHeaderText?: unknown;
  colorText?: unknown;
  dataApiEndpoint: string;
  disableAnalytics: boolean;
  eventId?: unknown;
  fontFamily?: string;
  oddsFormat?: unknown;
  group?: unknown;
  playerIds?: unknown;
  props?: unknown;
  recommendedMarketId?: unknown;
  show?: string;
  state?: unknown;
  repub?: unknown;
  teamIds?: unknown;
  noDataAvailableFallbackText?: unknown;
  imageType?: string;
  participant?: unknown;
  player?: unknown;
  sport?: string | null;
  competition?: string | null;
  analyticsDataLayer?: unknown;
  subeventId?: string;
};

const contract = async (
  {
    affIds,
    apiKey: affiliateKey,
    analytics,
    campaignName,
    categoryId,
    colorBackground /* marked for theming */,
    colorHeader /* marked for theming */,
    colorHeaderText /* marked for theming */,
    colorText /* marked for theming */,
    eventId,
    fontFamily /* marked for theming */,
    geolocation,
    groups,
    hash: widgetSlot,
    marketType = 'fixed',
    noDataAvailableFallbackText,
    oddsFormat,
    playerIds,
    playerProps,
    recommendedMarketId,
    repub,
    showNflLogosAndImages,
    subeventId,
    teamIds,
    variation
  }: ContractParams,
  attr: HTMLElement
) => {
  loadExternalFonts();

  const props: ContractProps = {
    analyticsImpressionEventData: {
      campaignName,
      widgetType: 'value-bet',
      variation: variation ?? '',
      widgetSlot,
      recommendedMarketId
    },
    affiliateKey,
    affiliates: affIds,
    categoryId,
    color: colorHeader,
    colorBackground,
    colorHeaderText,
    colorText,
    dataApiEndpoint: Q4ApiHost || window.location.origin,
    disableAnalytics: !analytics,
    eventId,
    fontFamily,
    oddsFormat,
    group: groups,
    playerIds,
    props: playerProps,
    recommendedMarketId,
    show: variation,
    state: geolocation,
    repub,
    teamIds,
    noDataAvailableFallbackText,
    ...(!showNflLogosAndImages && {
      imageType: 'avatar'
    })
  };

  const contextualAttributes = {
    sport: attr.getAttribute('sport'),
    competition: attr.getAttribute('competition'),
    player: attr.getAttribute('player'),
    participant: attr.getAttribute('participant')
  };

  // Handle contextual properties
  if (marketType === 'contextual') {
    props.participant = commaSeparatedValToArrOrVal(
      contextualAttributes.participant
    );
    props.player = commaSeparatedValToArrOrVal(contextualAttributes.player);
    props.sport = contextualAttributes.sport;
    props.competition = contextualAttributes.competition;
  }

  if (analytics) {
    props.analyticsDataLayer = analytics.getDataLayer();
  }

  if (subeventId) {
    props.subeventId = `oc-${subeventId}`;
  }

  if (fontFamily) {
    loadCustomGoogleFont(fontFamily);
  }

  return props;
};

const fetchCampaign = (element: HTMLElement) => {
  const baseAttributes = {
    slotId: element.getAttribute('slot-id'),
    overrideUrl: element.getAttribute('override-url')
  };

  const contextualAttributes = {
    sport: element.getAttribute('sport'),
    competition: element.getAttribute('competition'),
    player: element.getAttribute('player'),
    participant: element.getAttribute('participant')
  };

  const isContextual = !!(
    contextualAttributes.sport ||
    contextualAttributes.competition ||
    contextualAttributes.player ||
    contextualAttributes.participant
  );

  return fetchCampaignData(
    baseAttributes.slotId,
    baseAttributes.overrideUrl,
    isContextual ? contextualAttributes : undefined,
    element
  );
};

const customCss = `:host {
    box-sizing: border-box;
    display: block;
    width: 100%;
    height: 100%;
  }

  #root {
    width: inherit;
    height: inherit;
  }`;

type QueryKeyParams = {
  apiKey?: unknown;
  eventId?: unknown;
  variation?: unknown;
  playerProps?: unknown;
};

const queryKey = ({
  apiKey,
  eventId,
  variation,
  playerProps
}: QueryKeyParams) => {
  return [
    'value-bet',
    apiKey,
    eventId,
    playerProps ? JSON.stringify(playerProps) : null,
    variation
  ].filter(Boolean);
};

widgetBootstrap({
  attributes: meta.attributes,
  contract,
  customCss,
  Component,
  style: ValueBetCss,
  fetchCampaign,
  loader,
  name: widgetName,
  themes,
  themeContract,
  queryKey,
  version: '1.1.0' // NOTE: This value should match the last version from the widget's CHANGELOG.md file.
});
